import React, { useMemo } from 'react';
import { SystemCard } from '../components/SystemCard';
import { SystemCardPopoverContent } from '../components/SystemCardPopoverContent';
import { SpecializedEquipmentWithMetrics } from '../types';
import { iconForEnergySink, isPassiveTechnology } from '../utils';

const SPECIALIZED_SYSTEM_IMAGE = (
  <svg
    width='1em'
    height='1em'
    viewBox='0 0 61 61'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
  >
    <path
      d='M30.5 2C14.2076 2 1 15.2076 1 31.5C1 47.7924 14.2076 61 30.5 61C46.7924 61 60 47.7924 60 31.5C60 15.2076 46.7924 2 30.5 2ZM30.5 56.5C16.7076 56.5 5.5 45.2924 5.5 31.5C5.5 17.7076 16.7076 6.5 30.5 6.5C44.2924 6.5 55.5 17.7076 55.5 31.5C55.5 45.2924 44.2924 56.5 30.5 56.5Z'
      fill='currentColor'
    />
    <path
      d='M30.5 13C21.9396 13 15 19.9396 15 28.5V34.5C15 36.433 16.567 38 18.5 38H42.5C44.433 38 46 36.433 46 34.5V28.5C46 19.9396 39.0604 13 30.5 13ZM41.5 33.5H19.5V28.5C19.5 22.4289 24.4289 17.5 30.5 17.5C36.5711 17.5 41.5 22.4289 41.5 28.5V33.5Z'
      fill='currentColor'
    />
    <path
      d='M23.5 42.5H37.5C38.8807 42.5 40 43.6193 40 45V48.5C40 49.8807 38.8807 51 37.5 51H23.5C22.1193 51 21 49.8807 21 48.5V45C21 43.6193 22.1193 42.5 23.5 42.5Z'
      fill='currentColor'
    />
  </svg>
);

type SpecializedUnitProps = {
  equipmentUnit: SpecializedEquipmentWithMetrics;
  selected: boolean;
  count?: number;
  isLoading?: boolean;
  readOnly?: boolean;
  actions: {
    onAdd?: () => void;
    onIncrement?: () => void;
    onDecrement?: () => void;
    onRemove?: () => void;
    onChangeCount?: (newCount: number) => void;
  };
};

export const SpecializedUnit: React.FC<SpecializedUnitProps> = React.memo(
  ({ equipmentUnit, selected, count, isLoading, actions, readOnly }) => {
    const EnergyIcon = iconForEnergySink(equipmentUnit.energySink);

    const icons = useMemo(
      () => ({
        EnergyIcon: EnergyIcon,
        energyIconCount: equipmentUnit.relativeEnergyDemand,
        costIconCount: equipmentUnit.relativeCost,
      }),
      [
        EnergyIcon,
        equipmentUnit.relativeEnergyDemand,
        equipmentUnit.relativeCost,
      ],
    );

    const hoverPopover = useMemo(
      () => (
        <SystemCardPopoverContent
          coveragePercentage={equipmentUnit.coveragePercentage}
          name={equipmentUnit.name}
          energySource={equipmentUnit.energySink}
          EnergySourceIcon={EnergyIcon}
          energyDemand={Number(equipmentUnit.energyUsePerYr)}
          capitalCost={Number(equipmentUnit.capitalCostPerUnit)}
          annualCost={Number(equipmentUnit.maintenanceCostPerUnit)}
          coverageFieldTitle={'specialized equipment'}
          designLife={equipmentUnit.designLife}
          isLoading={isLoading}
        />
      ),
      [equipmentUnit, EnergyIcon, isLoading],
    );

    return (
      <SystemCard
        actions={actions}
        count={count}
        limit={equipmentUnit.limit}
        name={equipmentUnit.name}
        selected={selected}
        systemImage={SPECIALIZED_SYSTEM_IMAGE}
        icons={icons}
        hoverPopover={hoverPopover}
        readOnly={readOnly}
        unitsFixed={isPassiveTechnology(equipmentUnit)}
      />
    );
  },
);

SpecializedUnit.displayName = 'SpecializedUnit';
